import { type CSSProperties, useRef, useState } from "react";
import { BrokerFlow } from "@/components/BrokerFlow";
import { Faqs } from "@/components/Faqs";
import { Features } from "@/components/Features";
import { FinalCta } from "@/components/FinalCta";
import { Footer } from "@/components/Footer";
import { Hero } from "@/components/Hero";
import { Nav } from "@/components/Nav";
import { Problem } from "@/components/Problem";
import { TwoFlows } from "@/components/TwoFlows";
import { AUDIENCE_ACCENT, type Audience } from "@/content";
import { useOnScreen } from "@/hooks/useOnScreen";

/**
 * Ciam pre-launch landing. Single page, no router, light theme only. An
 * audience switch (printer vs. broker) drives the hero, problem, how-it-works
 * and feature set — and recolors the whole page by overriding `--accent` at the
 * root, so the brand shifts like a theme. The switch lives in the hero and is
 * mirrored into the sticky nav once it scrolls away. Lead capture is a stub.
 */
export default function App() {
  const [audience, setAudience] = useState<Audience>("imprenta");
  const heroSwitchRef = useRef<HTMLDivElement>(null);
  const heroSwitchOnScreen = useOnScreen(heroSwitchRef);

  // Switching audience re-tailors the top of the page; scroll back up so the
  // new version is read from its start (matters when toggling from the nav).
  const handleAudienceChange = (next: Audience) => {
    setAudience(next);
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: prefersReduced ? "auto" : "smooth" });
  };

  const themeStyle = {
    "--accent": AUDIENCE_ACCENT[audience],
    "--focus": AUDIENCE_ACCENT[audience],
  } as CSSProperties;

  return (
    <div className="min-h-screen bg-[var(--background)]" style={themeStyle}>
      <Nav
        audience={audience}
        onAudienceChange={handleAudienceChange}
        showSwitch={!heroSwitchOnScreen}
      />
      <main>
        <Hero
          audience={audience}
          onAudienceChange={handleAudienceChange}
          switchRef={heroSwitchRef}
        />
        <Problem audience={audience} />
        {audience === "imprenta" ? <TwoFlows /> : <BrokerFlow />}
        <Features audience={audience} />
        <Faqs audience={audience} />
        <FinalCta audience={audience} />
      </main>
      <Footer />
    </div>
  );
}
